<script setup lang="ts">
import { computed } from 'vue';
import { useRoute } from 'vue-router';
import { DgrIcon } from '@stockmarkteam/donguri-ui';
import LeftSectionMenu from '@/components/menu/left-section-menu.vue';
import SearchMenu from '@/components/menu/search-menu/search-menu.vue';
import AdminMenu from '@/components/menu/sections/admin-menu.vue';
import GroupMenu from '@/components/menu/sections/group-menu.vue';
import MyPageMenu from '@/components/menu/sections/my-page-menu.vue';
import NewsMenu from '@/components/menu/sections/news-menu.vue';
import OthersMenu from '@/components/menu/sections/others-menu.vue';
import SurveyMenu from '@/components/menu/sections/survey-menu.vue';
import {
  useActiveMenu,
  useMenuSection,
} from '@/utils/composables/useMenuSection';
import { useUserInfo } from '@/utils/swr';

interface Props {
  isMenuVisible: boolean;
  isExpanded: boolean;
}
defineProps<Props>();

defineEmits(['toggleIsFolded']);

const route = useRoute();

const { data: userInfo } = useUserInfo();

const section = useMenuSection();

const isAdminPages = computed(() => section.value === 'admin');
const isNewsPages = computed(() => section.value === 'news');
const isSearchPages = computed(() => section.value === 'search');
const isMyPage = computed(() => section.value === 'my_page');
const isGroupPage = computed(() => section.value === 'group');
const isSurveyPage = computed(() => section.value === 'survey');
const isOtherPage = computed(() => section.value === 'others');

const isExpandable = computed(() => {
  // 左サイドメニューにないページは折りたたみボタンを表示しない(一部pageのみ対応)
  // NOTE: https://anews.atlassian.net/browse/ANDEV-4421?focusedCommentId=39764
  switch (route.meta.type) {
    case 'notifications':
    case 'user':
      return false;
    default:
      return true;
  }
});
const activeMenu = useActiveMenu();
</script>

<template>
  <div class="o-left-menu" v-if="userInfo">
    <LeftSectionMenu :active-menu="activeMenu"></LeftSectionMenu>
    <KeepAlive>
      <template v-if="isMenuVisible">
        <AdminMenu class="left-menu__wrapper" v-if="isAdminPages" />
        <SearchMenu class="left-menu__wrapper" v-else-if="isSearchPages" />
        <NewsMenu class="left-menu__wrapper" v-else-if="isNewsPages" />
        <GroupMenu class="left-menu__wrapper" v-else-if="isGroupPage" />
        <MyPageMenu class="left-menu__wrapper" v-else-if="isMyPage" />
        <OthersMenu class="left-menu__wrapper" v-else-if="isOtherPage" />
        <SurveyMenu class="left-menu__wrapper" v-else-if="isSurveyPage" />
      </template>
    </KeepAlive>

    <button
      v-if="isExpandable"
      v-tooltip.top="!isExpanded ? '開いたままにする' : '折りたたむ'"
      class="left-menu-arrow c-text-regular--s"
      :class="{ 'visible-menu': isMenuVisible }"
      @click="$emit('toggleIsFolded')"
    >
      <DgrIcon
        size="small"
        :name="`chevron-${isExpanded ? 'left' : 'right'}`"
        class="small"
      />
    </button>
  </div>
</template>
<style lang="scss" scoped>
div.o-left-menu {
  display: flex;
  color: $color-gray1000;
}
.left-menu__wrapper {
  position: fixed;
  top: $header-height;
  left: $left-section-width;
}

.left-menu-arrow {
  list-style: none;
  cursor: pointer;
  position: absolute;
  bottom: 8px;
  left: 20px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  width: 40px;
  height: 40px;
  border-radius: 4px;
  background-color: white;
  border: none;

  &:hover {
    background: $color-link-menu-hover;
  }

  &.visible-menu {
    left: 320px;
  }
}

.left-menu-arrow:hover {
  background-color: $color-gray200;
}
</style>
